import { useQuery } from '@tanstack/react-query'
import { Calendar, Flag, Lock, Repeat as RepeatIcon } from 'lucide-react'
import { Modal } from './ui/Modal'
import { Avatar } from './Avatar'
import { api } from '../lib/api'
import { teamTaskDetailResponse } from '../lib/types'
import { formatDate, formatDateTime } from '../lib/format'

/**
 * Read-only task-details popup for the Team Tasks page — laid out to MIRROR the
 * Space page's task drawer (same field rows, Repeat, Description, Files,
 * Comments, Activity headings) so it reads as the same popup.
 *
 * Team Tasks lists tasks across the whole team, including tasks in private
 * Spaces the viewer isn't a member of. Navigating to the Space page for those
 * bounced the user to the Dashboard (the Space GET 403s). This modal shows the
 * details in place instead, fed by `GET /team/tasks/:taskId` (authorized by
 * team visibility, NOT Space membership). It is READ-ONLY — no edits — so a
 * supervisor can view a task without write access to a Space they aren't in.
 */

const PRIORITY_LABEL = {
  urgent: 'Urgent',
  high: 'High',
  normal: 'Medium',
  low: 'Low',
}
const PRIORITY_COLOR = {
  urgent: 'var(--color-priority-urgent)',
  high: 'var(--color-priority-high)',
  normal: 'var(--color-priority-medium)',
  low: 'var(--color-priority-low)',
}

/** One label/value pair — matches the drawer's row: muted label left, value
 *  right. */
function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 min-w-0">
      <span className="text-sm text-[color:var(--color-ink-muted)] shrink-0">
        {label}
      </span>
      <span className="text-sm text-[color:var(--color-ink)] text-right [overflow-wrap:anywhere] min-w-0">
        {children}
      </span>
    </div>
  )
}

/** Section heading — uppercase, muted, matching the drawer's DESCRIPTION /
 *  FILES & LINKS / COMMENTS / ACTIVITY headers. */
function SectionHeading({ children }) {
  return (
    <div className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-2">
      {children}
    </div>
  )
}

/** A date value with the drawer's "and time" semantics: a calendar icon when
 *  unset, the date (+ time when the user set one) otherwise. */
function DateValue({ iso, hasTime }) {
  if (!iso) {
    return <Calendar size={15} className="text-[color:var(--color-ink-muted)]" />
  }
  return <>{hasTime ? formatDateTime(iso) : formatDate(iso)}</>
}

export function TeamTaskDetailModal({ taskId, onClose }) {
  const open = Boolean(taskId)

  const query = useQuery({
    queryKey: ['team', 'task-detail', taskId],
    queryFn: async () => {
      const res = await api.get(`/team/tasks/${taskId}`)
      return teamTaskDetailResponse.parse(res.data)
    },
    enabled: open,
  })

  const t = query.data?.task ?? null
  const priority = t?.priority ?? null
  const assignees = t?.assignees ?? []

  const divider = 'border-t border-[color:var(--color-rule)]'

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t?.name ?? 'Task Details'}
      size="lg"
      dismissOnBackdrop
    >
      {query.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] py-6 text-center">
          Loading task…
        </p>
      ) : query.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)] py-6 text-center">
          This task could not be loaded, or it is no longer visible to your
          team.
        </p>
      ) : t ? (
        <div className="flex flex-col">
          {/* Read-only notice. */}
          <div className="mb-3 flex items-center gap-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2 text-xs text-[color:var(--color-ink-muted)]">
            <Lock size={13} className="shrink-0" />
            Read-only view. Open the task in its Space to make changes.
          </div>

          {/* Field rows — two columns, mirroring the Space drawer. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            <Row label="Created by">
              {t.createdByName ? (
                <span className="inline-flex items-center gap-1.5">
                  <Avatar
                    name={t.createdByName}
                    avatarUrl={t.createdByAvatarUrl}
                    size="xs"
                  />
                  {t.createdByName}
                </span>
              ) : (
                '—'
              )}
            </Row>
            <Row label="Assignees">
              {assignees.length === 0 ? (
                <span className="text-[color:var(--color-ink-muted)]">—</span>
              ) : (
                <span className="inline-flex items-center gap-1 flex-wrap justify-end">
                  {assignees.map((a) => (
                    <Avatar
                      key={a.userId}
                      name={a.fullName}
                      avatarUrl={a.avatarUrl}
                      size="xs"
                      colorByName
                    />
                  ))}
                </span>
              )}
            </Row>

            <Row label="Status">
              <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold"
                style={{
                  color: t.statusColor ?? 'var(--color-ink)',
                  backgroundColor: t.statusColor
                    ? `color-mix(in srgb, ${t.statusColor} 12%, transparent)`
                    : 'var(--color-canvas)',
                }}
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{
                    backgroundColor: t.statusColor ?? 'var(--color-ink-muted)',
                  }}
                />
                {t.status}
              </span>
            </Row>
            <Row label="Priority">
              {priority ? (
                <span
                  className="inline-flex items-center gap-1"
                  style={{ color: PRIORITY_COLOR[priority] }}
                >
                  <Flag
                    size={13}
                    fill={PRIORITY_COLOR[priority]}
                    strokeWidth={0}
                  />
                  {PRIORITY_LABEL[priority] ?? priority}
                </span>
              ) : (
                '—'
              )}
            </Row>

            <Row label="Start Date and Time">
              <DateValue iso={t.startDate} hasTime={t.startDateHasTime} />
            </Row>
            <Row label="Due Date and Time">
              <DateValue iso={t.dueDate} hasTime={t.dueDateHasTime} />
            </Row>

            <Row label="Date Created">
              {t.createdAt ? formatDateTime(t.createdAt) : '—'}
            </Row>
            <Row label="Date Updated">
              {t.updatedAt ? formatDateTime(t.updatedAt) : '—'}
            </Row>
          </div>

          {/* Repeat — read-only (no "Set repeat" here). */}
          <div className={`mt-2 pt-3 ${divider} flex items-center gap-2 text-sm text-[color:var(--color-ink-muted)]`}>
            <RepeatIcon size={15} />
            Repeat
            <span className="ml-1 text-[color:var(--color-ink)]">
              {t.recurring === 'active'
                ? 'On'
                : t.recurring === 'paused'
                  ? 'Paused'
                  : 'Off'}
            </span>
          </div>

          {/* Description. */}
          <div className={`mt-4 pt-4 ${divider}`}>
            <SectionHeading>Description</SectionHeading>
            {t.description && t.description.trim().length > 0 ? (
              <div className="rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2 text-sm text-[color:var(--color-ink)] whitespace-pre-wrap">
                {t.description}
              </div>
            ) : (
              <span className="text-sm text-[color:var(--color-ink-muted)]">
                No description.
              </span>
            )}
          </div>
        </div>
      ) : null}
    </Modal>
  )
}
